import React from 'react';

import {
    PinoMonthlyProduct,
    PinoProduct,
    PinoProductDetails,
    PinoProductTitle,
    Product,
    TggMembershipType,
    TggPinoProductTitle,
    getBafBenefitsHeadings,
    getBenefitsHeadingsForTable,
    dayPassBenefitsHeadings,
    getShortenedStudentBenefitsHeadings,
    getStudentBenefitsHeadings,
    getBafShortenedBenefitsHeadings,
    dayPassBenefitsHeadingsMbtV3,
    getShortenedBenefitsHeadings,
} from '@tgg/common-types';

import { PinoProductWithDetails } from './PinoTable.types';

const additionalBenefitsButtonHeader = 'Additional benefits';
const benefitToDisplayNumber = 7;

export const shouldDisableProduct = (
    product: Product,
    disabledProduct: TggMembershipType | undefined,
) => {
    if (!disabledProduct) {
        return false;
    }

    return product.membershipType === disabledProduct;
};

export const getKickerRibbonTitle = (kickerPriceDuration: number | null) => {
    const kickerPriceMonthName =
        kickerPriceDuration === 1 ? '1st' : kickerPriceDuration;
    return `${kickerPriceMonthName} month offer`;
};

export const getInitialSelectedProduct = (
    disabledProduct?: TggMembershipType,
    isStudentPinoTable?: boolean,
    isDayPassPinoTable?: boolean,
): PinoProductTitle => {
    if (isStudentPinoTable) {
        return TggPinoProductTitle.Student;
    }

    if (isDayPassPinoTable) {
        return TggPinoProductTitle.DayPass;
    }

    if (!disabledProduct) {
        return TggPinoProductTitle.Ultimate;
    }

    switch (disabledProduct) {
        case TggMembershipType.UltimateMonthly: {
            return TggPinoProductTitle.Standard;
        }
        case TggMembershipType.StandardMonthly: {
            return TggPinoProductTitle.Ultimate;
        }
        case TggMembershipType.OffPeakMonthly: {
            return TggPinoProductTitle.Ultimate;
        }
        case TggMembershipType.StudentFitquestGymAccess: {
            return TggPinoProductTitle.Student;
        }
        default: {
            return TggPinoProductTitle.Ultimate;
        }
    }
};

export const scrollToRelatedSection = (href: string) => {
    const targetSectionToScrollTo = document.querySelector(href);

    const NAVBAR_HEIGHT = 100;

    if (targetSectionToScrollTo) {
        const offset =
            targetSectionToScrollTo.getBoundingClientRect().top +
            window.pageYOffset -
            NAVBAR_HEIGHT;

        window.scrollTo({ top: offset, behavior: 'smooth' });
    }
};

const sortPINOProductByPosition = (
    currentProduct: PinoProductWithDetails,
    previousProduct: PinoProductWithDetails,
) => currentProduct.position - previousProduct.position;

export const mapPinoProductsWithDetails = (
    products: PinoMonthlyProduct[] | PinoProduct[],
    details: PinoProductDetails,
): PinoProductWithDetails[] => {
    const mappedPinoProducts = products.map(product => ({
        ...product,
        includedBenefits: details[product.membershipType].includedBenefits,
        title: details[product.membershipType].title,
        position: details[product.membershipType].position,
    }));

    return mappedPinoProducts.sort(sortPINOProductByPosition);
};

export const getVisibleBenefits = (
    isMBTOptimization: boolean,
    isMBTOptimizationV1 = false,
    isMBTOptimizationV2 = false,
    isStudentPinoTable?: boolean,
    isDayPassPinoTable?: boolean,
    isBenefitsShortened?: boolean,
    isGuestPassEnabled?: boolean,
    isMBTOptimizationV3?: boolean,
): string[] => {
    let benefits;

    if (isStudentPinoTable) {
        benefits =
            !isMBTOptimization && isBenefitsShortened
                ? Object.keys(
                      getShortenedStudentBenefitsHeadings(
                          isMBTOptimization,
                          isMBTOptimizationV2,
                      ),
                  )
                : getStudentBenefitsHeadings(
                      isMBTOptimization,
                      isMBTOptimizationV2,
                      isMBTOptimizationV3,
                  );
    } else if (isDayPassPinoTable) {
        benefits = isMBTOptimizationV3
            ? dayPassBenefitsHeadingsMbtV3
            : dayPassBenefitsHeadings;
    } else if (isGuestPassEnabled) {
        benefits = isBenefitsShortened
            ? Object.keys(
                  getShortenedBenefitsHeadings(
                      isMBTOptimization,
                      isMBTOptimizationV1,
                      isMBTOptimizationV2,
                      isMBTOptimizationV3,
                  ),
              )
            : getBenefitsHeadingsForTable(
                  isMBTOptimization,
                  isMBTOptimizationV1,
                  isMBTOptimizationV2,
                  isMBTOptimizationV3,
              );
    } else {
        benefits = isBenefitsShortened
            ? Object.keys(
                  getBafShortenedBenefitsHeadings(
                      isMBTOptimization,
                      isMBTOptimizationV1,
                      isMBTOptimizationV2,
                      isMBTOptimizationV3,
                  ),
              )
            : getBafBenefitsHeadings(
                  isMBTOptimization,
                  isMBTOptimizationV1,
                  isMBTOptimizationV2,
                  isMBTOptimizationV3,
              );
    }

    return benefits.slice(0, benefitToDisplayNumber);
};

export const getHiddenBenefits = (
    isMBTOptimization: boolean,
    isMBTOptimizationV1?: boolean,
    isMBTOptimizationV2?: boolean,
): string[] => {
    const benefits = getBenefitsHeadingsForTable(
        isMBTOptimization,
        isMBTOptimizationV1,
        isMBTOptimizationV2,
    );
    return benefits.length > benefitToDisplayNumber
        ? benefits.slice(benefitToDisplayNumber)
        : [];
};

export const getAdditionalBenefitButton = (
    isMBTOptimization: boolean,
): string[] => {
    const benefits = getBenefitsHeadingsForTable(isMBTOptimization);
    return benefits.length > benefitToDisplayNumber
        ? [additionalBenefitsButtonHeader]
        : [];
};

export const getHiddenBenefitCount = (isMBTOptimization: boolean): number => {
    const benefits = getBenefitsHeadingsForTable(isMBTOptimization);
    return benefits.length > benefitToDisplayNumber
        ? benefits.length - benefitToDisplayNumber
        : 0;
};

export function getTextFromReactNode(node: React.ReactNode): string {
    if (typeof node === 'string' || typeof node === 'number') {
        return node.toString();
    }

    if (Array.isArray(node)) {
        return node
            .map(child => getTextFromReactNode(child))
            .join(' ')
            .replaceAll(/\s+/g, ' ')
            .trim();
    }

    if (React.isValidElement(node)) {
        return getTextFromReactNode(node.props.children);
    }

    return '';
}
